/**
 * Ordem única do cardápio: categorias genéricas, o bloco de pizzas e o bloco de combos numa só
 * sequência, que o server manda em `menu.sections` para todas as lojas.
 *
 * Mesma regra do server, do app-client e do mobile-pos: segue a ordem recebida, descarta genérica
 * que não está em `menu.general` (removida, ou fora do resultado da busca) e manda para o fim o
 * que não foi listado — primeiro os blocos (combos, pizzas), depois as genéricas na ordem de
 * `menu.general`. Sem `sections` (cardápio em cache antigo), a ordem de sempre: combos, pizzas,
 * genéricas. Os destaques ficam fora: são sempre o topo.
 */
export type MenuSectionType = 'generic' | 'pizza' | 'combo';

export interface MenuSection {
  /** `generic:<id>`, `pizza` ou `combo`: estável para trackBy. */
  key: string;
  type: MenuSectionType;
  /** A categoria de `menu.general` (só em `generic`). */
  category: any;
}

const SECTION_TYPES: MenuSectionType[] = ['generic', 'pizza', 'combo'];
const DEFAULT_BLOCK_ORDER: MenuSectionType[] = ['combo', 'pizza'];

export function buildMenuSections(menu: any): MenuSection[] {
  const generalCategories: any[] = menu?.general || [];
  const receivedSections: any[] = Array.isArray(menu?.sections) ? menu.sections : [];
  const categoriesById = new Map<string, any>(generalCategories.map((category): [string, any] => [String(category.id), category]));
  const seenKeys = new Set<string>();
  const orderedSections: MenuSection[] = [];

  const pushSection = (type: MenuSectionType, categoryId: any = null) => {
    if (SECTION_TYPES.indexOf(type) === -1) return;
    if (type === 'generic' && !categoriesById.has(String(categoryId))) return;

    const sectionKey = type === 'generic' ? `generic:${categoryId}` : type;
    if (seenKeys.has(sectionKey)) return;

    seenKeys.add(sectionKey);
    orderedSections.push({
      key: sectionKey,
      type,
      category: type === 'generic' ? categoriesById.get(String(categoryId)) : null,
    });
  };

  receivedSections.forEach((receivedSection) => pushSection(receivedSection?.type, receivedSection?.id));
  DEFAULT_BLOCK_ORDER.forEach((blockType) => pushSection(blockType));
  generalCategories.forEach((category) => pushSection('generic', category.id));

  return orderedSections;
}

/**
 * A loja tirou combos/pizzas do topo (a ordem não começa por combos, pizzas)? Só então os
 * blocos saem do hero e entram na lista do render progressivo. Quem não reordenou continua
 * exatamente como antes — inclusive no HTML do SSR, onde o hero ancora o LCP.
 */
export function menuBlocksMoved(menu: any): boolean {
  const sections = buildMenuSections(menu);
  return !(sections[0]?.type === 'combo' && sections[1]?.type === 'pizza');
}

/** Valor da seção na barra de categorias e no scrollspy (o mesmo que a página já usava). */
export function menuSectionNavValue(section: MenuSection): string {
  if (section.type === 'combo') return 'combos';
  if (section.type === 'pizza') return 'pizzas';
  return `${section.category.name}-${section.category.id}`;
}
